import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import KanbanColumn from './KanbanColumn';

const useStyles = makeStyles(theme => ({
  scrollArea: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    ...theme.scrollbarStyles,
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  board: {
    display: 'flex',
    padding: theme.spacing(0.5),
    gap: theme.spacing(1.5),
    minHeight: '100%',
    width: 'max-content',
    minWidth: '100%',
  },
}));

const KanbanBoard = ({ lanes, onCardMove, onLaneReorder, updateTicket, isAdmin, onEditLane, onDeleteLane }) => {
  const classes = useStyles();
  const scrollAreaRef = useRef(null);
  const isDraggingRef = useRef(false);
  const pointerXRef = useRef(null);
  const dragScrollFrameRef = useRef(null);
  const pointerScrollRef = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  });

  const updatePointerX = event => {
    pointerXRef.current = event.clientX;
  };

  const updateTouchPointerX = event => {
    pointerXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const stopEdgeScroll = () => {
    isDraggingRef.current = false;
    pointerXRef.current = null;
    if (dragScrollFrameRef.current) {
      cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }
    window.removeEventListener('mousemove', updatePointerX);
    window.removeEventListener('touchmove', updateTouchPointerX);
  };

  const runEdgeScroll = () => {
    const scrollArea = scrollAreaRef.current;
    const pointerX = pointerXRef.current;

    if (!isDraggingRef.current || !scrollArea || pointerX == null) {
      dragScrollFrameRef.current = requestAnimationFrame(runEdgeScroll);
      return;
    }

    const rect = scrollArea.getBoundingClientRect();
    const edgeSize = 96;
    const maxStep = 26;
    let step = 0;

    if (pointerX > rect.right - edgeSize) {
      const distance = edgeSize - (rect.right - pointerX);
      step = Math.min(maxStep, Math.max(8, distance / 3));
    } else if (pointerX < rect.left + edgeSize) {
      const distance = edgeSize - (pointerX - rect.left);
      step = -Math.min(maxStep, Math.max(8, distance / 3));
    }

    if (step !== 0) {
      scrollArea.scrollLeft += step;
    }

    dragScrollFrameRef.current = requestAnimationFrame(runEdgeScroll);
  };

  const handleDragStart = () => {
    pointerScrollRef.current.active = false;
    isDraggingRef.current = true;
    window.addEventListener('mousemove', updatePointerX);
    window.addEventListener('touchmove', updateTouchPointerX, { passive: true });
    dragScrollFrameRef.current = requestAnimationFrame(runEdgeScroll);
  };

  const handleDragEnd = result => {
    stopEdgeScroll();

    if (!result.destination) return;

    const { source, destination, draggableId, type } = result;

    // Se for reordenação de lane
    if (type === 'LANE') {
      if (source.index !== destination.index) {
        onLaneReorder(source.index, destination.index);
      }
      return;
    }

    // Se for movimentação de card
    if (
      source.droppableId !== destination.droppableId ||
      source.index !== destination.index
    ) {
      onCardMove(
        draggableId,
        destination.droppableId,
        source.droppableId,
        source.index,
        destination.index
      );
    }
  };

  const handleMouseDown = event => {
    if (
      event.button !== 0 ||
      event.target.closest('[data-rbd-draggable-id]') ||
      event.target.closest('button') ||
      event.target.closest('input') ||
      event.target.closest('[role="button"]')
    ) {
      return;
    }

    pointerScrollRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: scrollAreaRef.current?.scrollLeft || 0,
      moved: false,
    };
  };

  const handleMouseMove = event => {
    const state = pointerScrollRef.current;
    const scrollArea = scrollAreaRef.current;

    if (!state.active || !scrollArea) return;

    const deltaX = event.clientX - state.startX;
    if (Math.abs(deltaX) > 3) {
      state.moved = true;
    }

    if (state.moved) {
      event.preventDefault();
      scrollArea.scrollLeft = state.scrollLeft - deltaX;
    }
  };

  const stopPointerScroll = () => {
    pointerScrollRef.current.active = false;
  };

  useEffect(() => {
    return () => {
      stopEdgeScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classes.scrollArea}
      ref={scrollAreaRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={stopPointerScroll}
      onMouseLeave={stopPointerScroll}
    >
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId="lanes" direction="horizontal" type="LANE">
          {(provided, snapshot) => (
            <div
              className={classes.board}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {lanes.map((lane, index) => (
                <KanbanColumn
                  key={lane.id}
                  id={lane.id}
                  title={lane.title}
                  tickets={lane.tickets}
                  color={lane.color}
                  index={index}
                  updateTicket={updateTicket}
                  isAdmin={isAdmin}
                  onEditLane={() => onEditLane(lane)}
                  onDeleteLane={() => onDeleteLane(lane)}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default KanbanBoard;
